'use client';

import { c as _c } from "react/compiler-runtime";
import type { ComponentProps, ReactNode } from 'react';
import { Box, type SxProps, Typography } from '@mui/material';
import { memo } from 'react';
import { palette } from '../../theme';
export type SelectablePillInputType = 'checkbox' | 'radio';
export type SelectablePillProps = ComponentProps<'input'> & {
  label: ReactNode;
  inputType?: SelectablePillInputType;
};
const PILL_STYLES = {
  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    'input:focus-visible + span': {
      outline: `2px solid ${palette.blues.light}`,
      outlineOffset: 2
    },
    'input:checked + span': {
      backgroundColor: palette.greens.lightest,
      borderColor: palette.primary.main,
      boxShadow: `inset 0 0 0 2px ${palette.primary.main}`,
      color: palette.primary.main,
      '&:hover:not(:disabled)': {
        backgroundColor: palette.greens.lighter
      }
    }
  },
  input: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: 0,
    padding: 0,
    border: 0,
    opacity: 0,
    pointerEvents: 'none'
  },
  content: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    py: 2,
    height: '40px',
    borderRadius: '99px',
    boxShadow: `inset 0 0 0 1px ${palette.neutral.lighter}`
  },
  unchecked: {
    backgroundColor: palette.common.white,
    borderColor: palette.neutral.lighter,
    color: palette.text.secondary,
    '&:hover:not(:disabled)': {
      backgroundColor: palette.neutral.lightest,
      borderColor: palette.neutral.light
    }
  }
} as const satisfies Record<string, SxProps>;
export const SelectablePill = memo(function SelectablePill(t0) {
  const $ = _c(26);
  let checked;
  let id;
  let inputProps;
  let label;
  let name;
  let onChange;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      checked,
      id,
      inputType: t1,
      label,
      name,
      onChange,
      value,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = id;
    $[3] = inputProps;
    $[4] = label;
    $[5] = name;
    $[6] = onChange;
    $[7] = t1;
    $[8] = value;
  } else {
    checked = $[1];
    id = $[2];
    inputProps = $[3];
    label = $[4];
    name = $[5];
    onChange = $[6];
    t1 = $[7];
    value = $[8];
  }
  const inputType = t1 === undefined ? "checkbox" : t1;
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...PILL_STYLES.base
    };
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== checked || $[11] !== id || $[12] !== inputProps || $[13] !== inputType || $[14] !== name || $[15] !== onChange || $[16] !== value) {
    t3 = <Box component="input" id={id} type={inputType} name={name} checked={checked} value={value} onChange={onChange} sx={PILL_STYLES.input} {...inputProps} />;
    $[10] = checked;
    $[11] = id;
    $[12] = inputProps;
    $[13] = inputType;
    $[14] = name;
    $[15] = onChange;
    $[16] = value;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      ...PILL_STYLES.content,
      ...PILL_STYLES.unchecked
    };
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      color: "inherit",
      fontSize: "14px"
    };
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== label) {
    t6 = <Box component="span" sx={t4}><Typography component="span" variant="textSmallBold" sx={t5}>{label}</Typography></Box>;
    $[20] = label;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== id || $[23] !== t3 || $[24] !== t6) {
    t7 = <Box component="label" htmlFor={id} sx={t2}>{t3}{t6}</Box>;
    $[22] = id;
    $[23] = t3;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
});